/**
 * Pure mutation functions for selection-related actions.
 */

import type {IDocumentData} from "@buildwithflux/models";
import {isEqual} from "lodash";

/**
 * Handles the selectObjects action natively in Zustand.
 * This bypasses the Redux reducer for better performance and cleaner architecture.
 *
 * Selection actions don't need:
 * - withHistoryUpdate (no patches for undo/redo - selection changes aren't undoable in the traditional sense)
 * - Action records (shouldGenerateActionRecord: false)
 * - Timestamp updates (updatesDocumentTimestamp: false)
 *
 * @param document The current document state (not mutated)
 * @param selectedObjectUids The UIDs of objects to select
 * @returns The updated document state, or the same reference if no change
 */
export function handleSelectObjectsNatively(
    document: IDocumentData | null,
    selectedObjectUids: string[],
): IDocumentData | null {
    if (!document) {
        return document;
    }

    // Check if selection actually changed using the same logic as updateSelectedSubjects
    // This avoids unnecessary object creation and re-renders
    const currentSelection = document.selectedObjectUids;

    // Fast path: if lengths differ, selection definitely changed
    if (currentSelection?.length !== selectedObjectUids.length) {
        return {...document, selectedObjectUids};
    }

    // Slow path: deep compare if lengths are the same
    if (!isEqual(currentSelection, selectedObjectUids)) {
        return {...document, selectedObjectUids};
    }

    // No change - return the same reference for memoization
    return document;
}

/**
 * Pure mutation function: locks or unlocks objects in either schematic or PCB mode.
 * Exported for use by direct store methods.
 */
export function applyLockObjects(
    draft: IDocumentData,
    objectUids: string[],
    locked: boolean,
    mode: "schematic" | "pcb",
): void {
    if (mode === "schematic") {
        for (const uid of objectUids) {
            const element = draft.elements[uid];
            if (element) {
                element.locked = locked;
            }
        }
    } else {
        for (const uid of objectUids) {
            const node = draft.pcbLayoutNodes[uid];
            if (node) {
                node.locked = locked;
            }
        }
    }
}
